import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../axiosInstance/axiosInstance";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import { CollectionAccountResponse } from "../contracts/CollectionAccountResponse";
import { CollectionOnboardingLinkResponse } from "../contracts/CollectionOnboardingLinkResponse";
import { DebitAttemptResponse } from "../contracts/DebitAttemptResponse";
import { DebitMandateResponse } from "../contracts/DebitMandateResponse";
import { MandateRequestResponse } from "../contracts/MandateRequestResponse";
import {
  mapResponseToCollectionAccount,
  mapResponseToDebitMandate,
} from "../mappers/CollectionMapper";
import { CollectionAccount } from "../models/CollectionAccount";
import { DebitMandate } from "../models/DebitMandate";

const BASE = "/billing/collection";

export const fetchCollectionAccount = createAsyncThunk<
  CollectionAccount,
  void,
  { rejectValue: ApiErrorResponse }
>("collection/fetchAccount", async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<CollectionAccountResponse>(`${BASE}/account`);
    return mapResponseToCollectionAccount(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to load the collection account"));
  }
});

/** Creates the connected account on first call and returns Stripe's hosted onboarding URL. */
export const startCollectionOnboarding = createAsyncThunk<
  string,
  string,
  { rejectValue: ApiErrorResponse }
>("collection/startOnboarding", async (country, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<CollectionOnboardingLinkResponse>(
      `${BASE}/account/onboarding`,
      { country }
    );
    return response.data.url;
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to start Stripe onboarding"));
  }
});

/** Re-pulls the readiness flags from Stripe — used when returning from onboarding. */
export const refreshCollectionAccount = createAsyncThunk<
  CollectionAccount,
  void,
  { rejectValue: ApiErrorResponse }
>("collection/refreshAccount", async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<CollectionAccountResponse>(`${BASE}/account/refresh`);
    return mapResponseToCollectionAccount(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to refresh the collection account"));
  }
});

/** Current (non-revoked) mandate per member. */
export const fetchMandates = createAsyncThunk<
  DebitMandate[],
  void,
  { rejectValue: ApiErrorResponse }
>("collection/fetchMandates", async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<DebitMandateResponse[]>(`${BASE}/mandates`);
    return response.data.map(mapResponseToDebitMandate);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to load direct-debit mandates"));
  }
});

/** Creates/reuses the member's Pending mandate and emails them the setup link. */
export const requestMandate = createAsyncThunk<
  MandateRequestResponse,
  string,
  { rejectValue: ApiErrorResponse }
>("collection/requestMandate", async (memberId, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<MandateRequestResponse>(
      `${BASE}/members/${memberId}/mandate-request`
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to request the direct-debit mandate"));
  }
});

/** Explicit collect / retry of an Invoiced or Overdue Payment. */
export const collectPayment = createAsyncThunk<
  DebitAttemptResponse,
  string,
  { rejectValue: ApiErrorResponse }
>("collection/collectPayment", async (paymentId, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<DebitAttemptResponse>(
      `${BASE}/payments/${paymentId}/collect`
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to collect the payment"));
  }
});
